import { useState } from 'react';
import { Sheet } from '../ui/Sheet';
import { Button } from '../ui/Button';
import { buildDisclosure } from '../../lib/publishDisclosure';
import type { Channel } from '../../types';

interface Props {
  channel: Channel;
  open: boolean;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

// Studio v2: shown before a channel flips to public. Nothing is published
// until the owner ticks the acknowledgement and presses Publish.
export function PublishDisclosureSheet({ channel, open, busy, onCancel, onConfirm }: Props) {
  const [ack, setAck] = useState(false);
  const d = buildDisclosure(channel, window.location.origin);
  const close = () => {
    setAck(false);
    onCancel();
  };
  return (
    <Sheet open={open} onClose={close} title="Publish this channel?">
      <div className="flex flex-col gap-4 text-[14px]">
        <section aria-labelledby="pd-visible">
          <h3 id="pd-visible" className="font-semibold mb-1">Anyone will be able to see</h3>
          <ul className="m-0 pl-5 flex flex-col gap-1">
            {d.visible.map((i) => (
              <li key={i.label}>
                <span className="font-medium">{i.label}:</span> <span className="text-black/65 dark:text-white/65">{i.detail}</span>
              </li>
            ))}
          </ul>
        </section>
        <section aria-labelledby="pd-hidden">
          <h3 id="pd-hidden" className="font-semibold mb-1">Stays private</h3>
          <ul className="m-0 pl-5 flex flex-col gap-1">
            {d.hidden.map((i) => (
              <li key={i.label}>
                <span className="font-medium">{i.label}:</span> <span className="text-black/65 dark:text-white/65">{i.detail}</span>
              </li>
            ))}
          </ul>
        </section>
        {d.warnings.length > 0 && (
          <ul role="status" className="m-0 pl-5 text-amber-700 dark:text-amber-300">
            {d.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        )}
        <p className="m-0 text-black/60 dark:text-white/60 break-all">Public link: {d.url}</p>
        <label className="flex items-start gap-2">
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-1" />
          <span>I understand that this data becomes public.</span>
        </label>
        <div className="flex gap-2 justify-end">
          <Button variant="bordered" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setAck(false);
              onConfirm();
            }}
            disabled={!ack}
            loading={busy}
          >
            Publish
          </Button>
        </div>
      </div>
    </Sheet>
  );
}
